import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Brand } from '../../components/layout/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { subscriptionAPI } from '../../services/apiServices';
import { ArrowLeftIcon, CreditCardIcon, ShieldIcon, TrendingIcon } from '../../components/common/Icons';
import { formatPlanPrice, getPlanRule, getStoredRegion, REGIONS } from '../../utils/plans';

const PlanSelectionPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const { notify } = useFeedback();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const region = useMemo(() => getStoredRegion(), []);
  const regionInfo = REGIONS[region];
  const pendingRegistration = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('clarityPendingRegistration') || 'null'); } catch { return null; }
  }, []);

  const selectPlan = async (plan) => {
    if (!user && !pendingRegistration) {
      navigate('/register');
      return;
    }

    setLoadingPlan(plan);
    try {
      if (user) {
        await subscriptionAPI.select({ plan, region });
      } else {
        await register({ ...pendingRegistration, plan, region });
        sessionStorage.removeItem('clarityPendingRegistration');

        // Google Tag Manager — evento de cadastro concluído
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cadastro_concluido',
          plano: plan,
          regiao: region,
        });
      }
      await notify({
        variant: 'success',
        title: t('feedback.planSelectedTitle'),
        message: t('feedback.planSelectedMessage', { days: getPlanRule(region, plan).trialDays }),
      });
      navigate('/dashboard');
    } catch (error) {
      notify({
        variant: 'danger',
        title: t('feedback.planErrorTitle'),
        message: error.response?.data?.error || t('feedback.planErrorMessage'),
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900">
      <div className="mx-auto max-w-[860px]">
        <div className="mb-10 flex items-center gap-4">
          <Link to="/localidade" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100" aria-label={t('common.back')}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <Brand />
        </div>

        <div className="text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700">
            <span>{regionInfo.flag}</span>
            {regionInfo.name} • {regionInfo.currency}
          </div>
          <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900">{t('plans.title')}</h1>
          <TrialBillingNotice>{t('plans.trialBillingNotice')}</TrialBillingNotice>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <PlanCard
            icon={<TrendingIcon className="h-7 w-7" />}
            iconClass="bg-slate-100 text-slate-700"
            title={t('plans.basic.title')}
            price={formatPlanPrice(region, 'basic', i18n.language)}
            period={t('plans.month')}
            features={[
              t('plans.demo', { days: getPlanRule(region, 'basic').trialDays }),
              t('plans.allFeatures'),
              t('plans.basic.limit'),
            ]}
            buttonText={loadingPlan === 'basic' ? t('common.processing') : t('plans.chooseBasic')}
            onClick={() => selectPlan('basic')}
            disabled={Boolean(loadingPlan)}
          />

          <PlanCard
            highlighted
            icon={<ShieldIcon className="h-7 w-7" />}
            iconClass="bg-amber-50 text-amber-700"
            title={t('plans.standard.title')}
            price={formatPlanPrice(region, 'standard', i18n.language)}
            period={t('plans.month')}
            features={[
              t('plans.demo', { days: getPlanRule(region, 'standard').trialDays }),
              t('plans.allFeatures'),
              t('plans.standard.limit'),
            ]}
            buttonText={loadingPlan === 'standard' ? t('common.processing') : t('plans.chooseStandard')}
            onClick={() => selectPlan('standard')}
            disabled={Boolean(loadingPlan)}
            iconButton={<CreditCardIcon className="h-4 w-4" />}
          />
        </div>

        <p className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-600">
          <ShieldIcon className="h-4 w-4" />
          {t('plans.securePayment')}
        </p>
      </div>
    </main>
  );
};

const PlanCard = ({ highlighted, icon, iconClass, title, price, period, features, buttonText, onClick, disabled, iconButton }) => {
  const { t } = useTranslation();

  return (
    <section className={`relative rounded-lg border bg-white p-8 shadow-soft ${highlighted ? 'border-sky-500 ring-1 ring-sky-500' : 'border-slate-200'}`}>
      {highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sky-500 px-4 py-1 text-xs font-bold text-white">
          {t('plans.standard.title')}
        </span>
      )}
    <div className="flex items-center gap-4">
      <div className={`flex h-14 w-14 items-center justify-center rounded-xl ${iconClass}`}>{icon}</div>
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <p className="mt-1 text-2xl font-bold text-slate-900">{price}<span className="text-sm font-medium text-slate-600">{period}</span></p>
      </div>
    </div>
    <ul className="mt-8 space-y-4 text-sm text-slate-700">
      {features.map((feature) => (
        <li key={feature} className="flex items-center gap-3">
          <span className="text-emerald-600">✓</span>
          {feature}
        </li>
      ))}
    </ul>
    <button
      className={`mt-8 inline-flex h-12 w-full items-center justify-center gap-3 rounded-lg font-bold shadow-soft transition disabled:opacity-60 ${
        highlighted ? 'bg-sky-500 text-white hover:bg-sky-600' : 'border border-slate-200 bg-white text-slate-900 hover:border-sky-200 hover:text-sky-600'
      }`}
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      {iconButton}
      {buttonText}
    </button>
    </section>
  );
};


const TrialBillingNotice = ({ children }) => (
  <div
    data-testid="trial-billing-notice"
    className="mx-auto mt-5 max-w-[560px] rounded-lg border border-sky-100 bg-white/90 px-4 py-3 text-left shadow-sm"
  >
    <div className="flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600">
        <ShieldIcon className="h-4 w-4" />
      </span>
      <p className="whitespace-pre-line text-sm leading-6 text-slate-600">{children}</p>
    </div>
  </div>
);

export default PlanSelectionPage;
